import PropTypes from 'prop-types';
import React from 'react';
import Velocity from 'velocity-animate';

import Scrollbar from './scrollbar';

export default class Scrollable extends React.Component {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    className: PropTypes.string,
    onScroll: PropTypes.func,
    scrollTo: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.number,
      PropTypes.string,
    ]),
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    // This keeps track of timers
    this.timers = {};

    // This version only supports vertical scrolling,
    // but obviously more can be added.
    this.state = {
      blockEvents: false,
      classNames: [],
      scrubbing: false,
      thumbHeight: 0,
      thumbTop: 0,
      visibleScrollbar: false,
    };

    this.onScrub = this.onScrub.bind(this);
    this.onScrubEnd = this.onScrubEnd.bind(this);
    this.onScrubStart = this.onScrubStart.bind(this);
  }

  // When the component mounts we want to hide the native browser scrollbar,
  // and possibly force it start at the bottom position.
  componentDidMount() {
    if (this.contentNode) {
      // Hide the native scrollbar
      this.contentNode.style.marginRight = -Scrollbar.calculateWidth() + 'px';
    }

    // scroll to a position by default
    if (this.props.scrollTo) {
      const { target, ...args } = this.props.scrollTo;
      this.scrollTo(target, args);
    }

    this.shouldShowOverflow();
  }

  // Clear out our event listeners and timers
  componentWillUnmount() {
    clearTimeout(this.timers['initialScroll']);
    clearTimeout(this.timers['hideScrollbar']);
  }

  // Calculate the current scroll position.
  getScrollPosition() {
    return this.contentNode.scrollTop;
  }

  getScrollHeight() {
    return this.contentNode.scrollHeight;
  }

  getScrollState() {
    const scrollbar = this.scrollbarNode.node;
    const barHeight = scrollbar ? scrollbar.clientHeight : 0;
    const { clientHeight, scrollHeight, scrollTop } = this.contentNode;

    // Calculate scrolling position in %
    let scrollPos = (scrollTop / (scrollHeight - clientHeight)) * 100;

    // In some scenarios 0 - 0 = NaN so we force it to 100% then.
    if (isNaN(scrollPos)) {
      scrollPos = 100;
    }

    // Calculate vertical thumb height and position
    let thumbHeight = Math.max(
      30,
      Math.min(
        barHeight,
        100 * (clientHeight / (scrollHeight - clientHeight * 0.66))
      )
    );
    let thumbTop = Math.min(
      barHeight - thumbHeight,
      scrollPos * ((barHeight - thumbHeight) / 100)
    );

    // Don't show scrollbar if there is nothing to scroll.
    if (scrollHeight <= clientHeight) {
      thumbTop = thumbHeight = 0;
    }

    return {
      thumbTop,
      thumbHeight,
    };
  }

  // When scrubbing (dragging the scrollbar thing)
  onScrubStart() {
    this.setState({ scrubbing: true });
  }

  onScrubEnd() {
    this.setState({ scrubbing: false });
  }

  onScrub(thumb, delta) {
    const scrollbar = this.scrollbarNode.node;
    const barHeight = scrollbar ? scrollbar.clientHeight : 0;
    const { thumbHeight } = thumb;
    let { thumbTop } = thumb;
    const maxHeight = barHeight - thumbHeight;
    thumbTop += delta;
    thumbTop = Math.max(0, Math.min(maxHeight, thumbTop));
    this.setState({ thumbTop, thumbHeight });
    const percentage = thumbTop / maxHeight;

    const maxScroll =
      this.contentNode.scrollHeight - this.contentNode.offsetHeight;
    this.contentNode.scrollTop = maxScroll * percentage;
  }

  // We want people to be able to scroll to...
  // - React components
  // - Numbers! if you know where to go
  // - Query Selector strings - if you know what it's called
  // - Bottom
  // - Top
  scrollTo(child, options = {}) {
    Scrollable.scrollTo(this, child, options);
  }

  // Static scroll component
  static scrollTo(
    instance,
    orginal_child,
    {
      align = 'top',
      animated = false,
      duration = 200,
      offset = 0,
      blockEvents = false,
      onScrollingEnd = null,
    } = {}
  ) {
    const parent =
      instance instanceof Scrollable ? instance.contentNode : instance;
    let child = orginal_child;

    if (!parent || (!child && child !== 0)) {
      return;
    }

    let scrollTo = false;

    if (Number.isInteger(child)) {
      scrollTo = child;
    } else if (child === 'bottom') {
      scrollTo = parent.scrollHeight;
    } else if (child === 'top') {
      scrollTo = 0;
    } else if (typeof child === 'string') {
      child = parent.querySelector(child);
    }

    // Get the position for our HTML element
    if (child instanceof HTMLElement) {
      let childOffset = Scrollable.getChildOffset(parent, child);

      if (align === 'bottom') {
        childOffset = Math.max(
          childOffset - parent.offsetHeight + child.offsetHeight,
          0
        );
      } else if (align === 'center') {
        childOffset = Math.max(
          childOffset - parent.offsetHeight / 2 + child.offsetHeight / 2,
          0
        );
      }
      childOffset += offset;
      // Keep offset in bounds
      scrollTo = Math.min(
        Math.max(0, childOffset),
        parent.scrollHeight - parent.offsetHeight
      );
    }

    // Nothing to scroll.
    if (scrollTo === false) {
      return;
    }

    if (scrollTo === parent.scrollTop) {
      return;
    }

    if (animated) {
      const tween = [scrollTo, parent.scrollTop];

      if (blockEvents && (!instance.state || !instance.state.blockEvents)) {
        instance.setState({ blockEvents: true });
      } else if (!blockEvents && instance.state && instance.state.blockEvents) {
        instance.setState({ blockEvents: false });
      }
      Velocity(parent, 'stop', true);
      Velocity(
        parent,
        { tween },
        {
          easing: [0.77, 0.0, 0.175, 1.0], // [cubic bezier quartz-in-out]
          duration,
          complete: () => {
            if (blockEvents) {
              instance.setState({ blockEvents: false });
            }

            if (typeof onScrollingEnd === 'function') {
              onScrollingEnd();
            }
          },
          progress: (els, c, r, s, t) => (els[0].scrollTop = t),
        }
      );
    } else {
      parent.scrollTop = scrollTo;
    }
  }

  // Find the offset for a specific child
  static getChildOffset(parent, original_node) {
    let node = original_node;
    let offset = 0;

    if (typeof node === 'string') {
      node = document.querySelector(node);
    }

    if (node && parent.contains(node)) {
      do {
        offset += node.offsetTop;
      } while (
        (node = node.offsetParent) !== parent &&
        node.offsetParent !== null
      );
    }

    return offset;
  }

  // Find the position for a specific node
  getNodePosition(node) {
    const top =
      Scrollable.getChildOffset(this.contentNode, node) -
      this.contentNode.scrollTop;

    return {
      top,
      bottom: this.contentNode.offsetHeight - top,
    };
  }

  isNodeInView(node) {
    const { top, bottom } = this.getNodePosition(node);
    const height = this.contentNode.offsetHeight;

    return top < height && bottom < height;
  }

  // Scroll event triggered
  handleScroll() {
    // Could be animating scroll.. don't
    Velocity(this.node, 'stop', true);

    // Disable horizontal scroll (can still happen with
    // certain input devices or text selection)
    if (this.contentNode.scrollLeft) {
      this.contentNode.scrollLeft = 0;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.contentNode;
    const scrollState = this.getScrollState();
    let updateDOM = scrollTop + clientHeight <= scrollHeight;
    // also don't update if there's a very small difference after decimal point
    // this avoids the jittery-ness
    updateDOM =
      updateDOM &&
      Math.ceil(this.state.thumbTop) !== Math.ceil(scrollState.thumbTop);

    // Mobile has it's own scrollbar that works fine, so we
    // don't need to do much else with that.
    if (!this.state.scrubbing && !/iPad|iPhone|iPod/.test(navigator.platform)) {
      // Get current position and update state
      if (updateDOM) {
        this.setState(scrollState);
      }
    }

    // Is there actual scrollable space and do we display scrollbar?
    if (updateDOM) {
      this.shouldShowOverflow();
    }

    // Do a callback, if needed
    if (this.state.blockEvents === false && this.props.onScroll) {
      this.props.onScroll({ clientHeight, scrollHeight, scrollTop });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.scrollTo && this.props.scrollTo !== prevProps.scrollTo) {
      const { target, ...args } = prevProps.scrollTo;
      this.scrollTo(target, args);
    }

    this.shouldShowOverflow();

    // Update scrollbar if content size changed
    if (this.contentNode && this.lastHeight !== this.contentNode.scrollHeight) {
      // Get current position and update state, this needs to happen on mount
      // so we ignore the warning from eslint in this case. Not ideal but otherwise
      // we cannot access the DOM properties.
      clearTimeout(this.timers['initialScroll']);
      this.timers['initialScroll'] = setTimeout(() => {
        this.setState(this.getScrollState());
      }, 10);
      this.lastHeight = this.contentNode.scrollHeight;
    }
  }

  shouldShowOverflow() {
    if (!this.contentNode) {
      return;
    }
    const iOSDevice = /iPad|iPhone|iPod/.test(navigator.platform);
    const classNames = [];

    // There is more to scroll on the bottom
    const scrollBottom =
      this.contentNode.scrollHeight - this.contentNode.clientHeight;

    if (
      this.contentNode.scrollHeight > this.contentNode.clientHeight &&
      this.contentNode.scrollTop < scrollBottom
    ) {
      classNames.push('scrollable--overflow-bottom');
    }

    // There is more to scroll on the top
    if (this.contentNode.scrollTop > 0) {
      classNames.push('scrollable--overflow-top');
    }

    // Update the state
    const visibleScrollbar = Boolean(classNames.length) && !iOSDevice;

    if (
      this.state.visibleScrollbar === visibleScrollbar &&
      this.state.classNames.length === classNames.length
    ) {
      return;
    }

    this.setState({
      visibleScrollbar,
      classNames,
    });
  }

  // Render our scrollable content thing
  render() {
    const className = ['scrollable'];

    if (this.props.className) {
      className.push(this.props.className);
    }
    className.push(...this.state.classNames);

    return (
      <div ref={(node) => (this.node = node)} className={className.join(' ')}>
        <div
          ref={(node) => (this.contentNode = node)}
          className="scrollable__content"
          onScroll={(e) => this.handleScroll(e)}
        >
          {this.props.children}
        </div>

        <Scrollbar
          ref={(node) => (this.scrollbarNode = node)}
          parent={this}
          top={this.state.thumbTop}
          height={this.state.thumbHeight}
          onScrubStart={this.onScrubStart}
          onScrubEnd={this.onScrubEnd}
          onScrub={this.onScrub}
          visible={this.state.visibleScrollbar}
        />
      </div>
    );
  }
}
